import { useMemo } from 'react';
import classnames from 'classnames';

import ChangeInfoText from '@components/change-info-text/ChangeInfoText';
import { SalesPerformance } from '@models/SalesPerformance';
import { FactRevenue } from '@utils/pageConfig/Types';
import { Kpi } from '@utils/Formatters';
import { GrowthTypes } from '@ecosystem/formatters';
import { getSalesMetrics, MarketShareType, POS_FACT_TILE, PosFactTileType } from './PosFactTileConfig';

export type PosFactTileProps = {
  salesPerformance: SalesPerformance;
  factRevenue: FactRevenue;
  kpiType: Kpi;
  tileTitle: string;
  selectedGrowthType: GrowthTypes;
  showMarketShare?: boolean;
  tileType: PosFactTileType;
  marketShareType: MarketShareType;
  showAnnualSummary?: boolean;
  showOnlyMarketShare?: boolean;
  showGrowthIcon?: boolean;
};

export const PosFactTile: React.FC<PosFactTileProps> = ({
  salesPerformance,
  factRevenue,
  kpiType,
  tileTitle,
  selectedGrowthType,
  showMarketShare = false,
  tileType,
  showAnnualSummary = false,
  showOnlyMarketShare = false,
  marketShareType,
  showGrowthIcon,
}) => {
  const { salesInfo, marketShareInfo, ytdInfo, mtdInfo } = useMemo(() => {
    const salesGrowth =
      selectedGrowthType === GrowthTypes.actual
        ? salesPerformance?.[tileType]?.growth?.value
        : salesPerformance?.[tileType]?.growth?.percentage;
    const salesValue = salesPerformance?.[tileType]?.revenue?.value;
    const sales = getSalesMetrics(
      kpiType,
      salesValue,
      salesGrowth,
      selectedGrowthType,
      factRevenue.currencySymbol,
      showGrowthIcon,
    );

    const marketShareGrowth = salesPerformance?.[marketShareType]?.selectedPeriod?.growthPercentage;
    const marketShareValue = salesPerformance?.[marketShareType]?.selectedPeriod?.sharePercentage;
    const marketShare = getSalesMetrics(
      Kpi.revenueShare,
      marketShareValue,
      marketShareGrowth,
      GrowthTypes.actual,
      factRevenue.currencySymbol,
      showGrowthIcon,
    );

    const annualSummaryGrowthType = showOnlyMarketShare ? GrowthTypes.actual : GrowthTypes.percentage;
    const annualSummaryKpiTYpe = showOnlyMarketShare ? Kpi.revenueShare : Kpi.revenue;

    const ytdGrowth = showOnlyMarketShare
      ? salesPerformance?.[marketShareType]?.ytd.growthPercentage
      : salesPerformance?.[tileType]?.ytd.percentage;
    const ytd = getSalesMetrics(
      annualSummaryKpiTYpe,
      null,
      ytdGrowth,
      annualSummaryGrowthType,
      factRevenue.currencySymbol,
      showGrowthIcon,
    );

    const matGrowth = showOnlyMarketShare
      ? salesPerformance?.[marketShareType]?.mat.growthPercentage
      : salesPerformance?.[tileType]?.mat.percentage;
    const mtd = getSalesMetrics(
      annualSummaryKpiTYpe,
      null,
      matGrowth,
      annualSummaryGrowthType,
      factRevenue.currencySymbol,
      showGrowthIcon,
    );

    return { salesInfo: sales, marketShareInfo: marketShare, ytdInfo: ytd, mtdInfo: mtd };
  }, [salesPerformance, selectedGrowthType]);

  return (
    <div>
      {!showOnlyMarketShare && (
        <div className="text-left">
          <div className="text-md">{tileTitle}</div>
          <div className="text-3xl font-bold">{salesInfo.kpiInfo}</div>
          <ChangeInfoText
            isPositiveGrowth={salesInfo.isPositiveGrowth}
            text={salesInfo.growthValue}
            showIcon={showGrowthIcon}
          />
        </div>
      )}
      {(showMarketShare || showOnlyMarketShare) && (
        <div
          className={classnames({
            'flex-column items-start': showOnlyMarketShare,
            'flex-column items-end absolute right-0 bottom-0': showMarketShare,
          })}
        >
          {<div className="text-md">{showOnlyMarketShare ? tileTitle : POS_FACT_TILE.Share}</div>}
          <div className={classnames('text-3xl', { 'text-3xl font-bold': showOnlyMarketShare })}>
            {marketShareInfo.kpiInfo}
          </div>
          <ChangeInfoText
            isPositiveGrowth={marketShareInfo.isPositiveGrowth}
            text={marketShareInfo.growthValue}
            showIcon={showGrowthIcon}
          />
        </div>
      )}
      {showAnnualSummary && (
        <div className="absolute right-0 bottom-0">
          <div className="flex gap-1 justify-end">
            <ChangeInfoText
              isPositiveGrowth={ytdInfo.isPositiveGrowth}
              text={ytdInfo.growthValue}
              showIcon={showGrowthIcon}
            />
            <span>{POS_FACT_TILE.Ytd}</span>
          </div>
          <div className="flex gap-1 justify-end">
            <ChangeInfoText
              isPositiveGrowth={mtdInfo.isPositiveGrowth}
              text={mtdInfo.growthValue}
              showIcon={showGrowthIcon}
            />
            <span>{POS_FACT_TILE.Mat}</span>
          </div>
        </div>
      )}
    </div>
  );
};
